import React from 'react';
import { useNavigate } from 'react-router-dom';
import { UserProfile } from '../../../types/types';
import { RestUser } from '../../../services/auth/types/user';
import { getAllTrips } from '../../../services/trips/api/tripsApi';
import DashboardLayout from '../../layouts/DashboardLayout';
import { ProfileTripPreview, mapTripToProfilePreview } from '../../utils/mapTripToProfilePreview';
import EmptyState from '../../components/common/EmptyState';
import './ProfileTripsPage.css';

interface ProfileTripsPageProps {
  user: UserProfile;
  restUser: RestUser | null;
}

const ProfileTripsPage: React.FC<ProfileTripsPageProps> = ({ user, restUser }) => {
  const navigate = useNavigate();
  const [profileTrips, setProfileTrips] = React.useState<ProfileTripPreview[]>([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const profileName = user?.name && !user.name.includes('@') ? user.name : '';
  const displayName = [restUser?.firstName, restUser?.lastName].filter(Boolean).join(' ') || profileName || 'Emma Wilson';

  const fetchProfileTrips = React.useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      // The full archive grid: walking the cursor keeps it COMPLETE. Before
      // 2026-08-03 this looked complete but silently stopped at DynamoDB's 1MB
      // response; now the truncation would be a visible 10.
      const fetchedTrips = await getAllTrips();
      setProfileTrips(fetchedTrips.map((trip, index) => mapTripToProfilePreview(trip, index)));
    } catch (err: any) {
      setError(err.message || 'Failed to load profile trips.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  React.useEffect(() => {
    fetchProfileTrips();
  }, [fetchProfileTrips]);

  const openTrip = (selectedTrip: ProfileTripPreview) => {
    navigate(`/trips/${selectedTrip.id}`, {
      state: { backTo: '/profile/trips', owned: true, trip: selectedTrip.sourceTrip },
    });
  };

  return (
    <DashboardLayout activeSection="settings" wide>
      <div className="v2-profile-trips-page">
        <button type="button" className="v2-profile-trips-page__back" onClick={() => navigate('/profile')}>
          ← Back to profile
        </button>

        <header className="v2-profile-trips-page__header">
          <p>{displayName}</p>
          <h1>All trips</h1>
          <span>{isLoading ? 'Loading trips...' : `${profileTrips.length} trips`}</span>
        </header>

        {isLoading && <ProfileTripsPageState message="Loading trips..." />}
        {!isLoading && error && (
          <ProfileTripsPageState message={error} actionLabel="Retry" onAction={fetchProfileTrips} />
        )}
        {!isLoading && !error && profileTrips.length === 0 && (
          <EmptyState
            title="No trips yet"
            body="Every trip you plan or get added to shows up on this page."
            action={{ label: 'Plan a trip', to: '/ask-ai' }}
          />
        )}
        {!isLoading && !error && profileTrips.length > 0 && (
          <section className="v2-profile-trips-page__grid" aria-label="All profile trips">
            {profileTrips.map((trip) => (
              <button
                type="button"
                key={trip.id}
                className="v2-profile-trips-page__card"
                onClick={() => openTrip(trip)}
              >
                <img src={trip.imageUrl} alt="" />
                <div>
                  <h2>{trip.title}</h2>
                  <p>{trip.meta}</p>
                  <small>{trip.description}</small>
                </div>
              </button>
            ))}
          </section>
        )}
      </div>
    </DashboardLayout>
  );
};

interface ProfileTripsPageStateProps {
  message: string;
  actionLabel?: string;
  onAction?: () => void;
}

const ProfileTripsPageState: React.FC<ProfileTripsPageStateProps> = ({ message, actionLabel, onAction }) => (
  <div className="v2-profile-trips-page__state">
    <p>{message}</p>
    {actionLabel && onAction && (
      <button type="button" onClick={onAction}>
        {actionLabel}
      </button>
    )}
  </div>
);

export default ProfileTripsPage;
